import React from "react";
import ContentWrapper from "../../components/layout/ContentWrapper";
import { useCustomTranslation } from "../../helpers/translateHelpers";
import InviteHeading from "./components/InviteHeading";
import { Button, useAuth } from "../../components";
import { useNavigate, useParams } from "react-router";
import { Trans } from "react-i18next";
import { InviteRouteParams } from "./models";
import customerUserClient from "../../api/private/customerUserClient";
import {
  saveAccessToken,
  saveRefreshToken,
  saveRefreshTokenId,
} from "../../helpers/tokenManager";
import { useQueryClient } from "@tanstack/react-query";
import { showToast } from "../../helpers/toastHelpers";
import { inviteKey } from "./queries";
import { handleApiErrorWithNotification } from "../../helpers/errorHandling";
import { NetworkError } from "../../common/models";

const InviteAcceptPage: React.FC = (): React.ReactElement => {
  const { user, onLogout, setOtherAccounts, setUser } = useAuth();
  const { tInvite, tError } = useCustomTranslation();
  const queryClient = useQueryClient();

  const params = useParams<InviteRouteParams>();
  const navigate = useNavigate();

  const handleLogoutAndRedirect = async () => {
    try {
      await onLogout();

      await navigate(
        `/${params.tenantCode}/invite/${params.invitationGuid}/register`,
      );
    } catch (e) {
      handleApiErrorWithNotification(e as NetworkError);
    }
  };

  const handleAcceptWithExistingAccount = async () => {
    try {
      const result = await customerUserClient.acceptInvite({
        invitationGuid: params.invitationGuid ?? "",
      });

      if (result.customerUser && result.success) {
        saveAccessToken(result.accessToken);
        saveRefreshToken(result.refreshToken);
        saveRefreshTokenId(result.refreshTokenId);
        setUser(result.customerUser);
        setOtherAccounts(result.contactPeople);

        await navigate("/");
        // Clear old data
        await queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] !== inviteKey,
        });
      } else {
        showToast(tError("customer_portal_accept_invite_failed"));
      }
    } catch {
      showToast(tError("customer_portal_accept_invite_failed"));
    }
  };

  return (
    <ContentWrapper largePadding htmlTitle={tInvite("html_title")}>
      <InviteHeading />
      <div className="pt-2 pb-4">
        <p className="mb-2">{tInvite("accept_description")}</p>
        <p className="mb-2">
          <Trans
            i18nKey="logged_user"
            ns="customerportal-invite"
            values={{
              username: user?.username,
            }}
          />
        </p>
        <Button
          label={tInvite("accept_invite_button")}
          mode="primary"
          onClick={handleAcceptWithExistingAccount}
        />
      </div>
      <hr className="border-gray bg-gray my-4" />
      <div>
        <p className="mb-2 pt-4">{tInvite("create_different_account")}</p>
        <Button
          label={tInvite("logout_and_create_button")}
          onClick={handleLogoutAndRedirect}
        />
      </div>
    </ContentWrapper>
  );
};

export default InviteAcceptPage;
